import { useCallback, useEffect, useEffectEvent, useMemo, useState } from 'react';

import type { ExtendedSeriesOptionsType } from '@/components/Chart/types/highcharts';
import { applySeriesVisibility, getSeriesId } from '@/components/Chart/utils/seriesVisibility';

const useSeriesVisibility = (series: ExtendedSeriesOptionsType[] | undefined) => {
  const [hiddenSeriesIds, setHiddenSeriesIds] = useState<string[]>([]);

  const pruneHiddenSeriesIds = useEffectEvent((availableIds: Set<string>) => {
    setHiddenSeriesIds(prev => {
      const next = prev.filter(id => availableIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  });

  useEffect(() => {
    if (!series?.length) return;
    const availableIds = new Set(
      series.map(item => getSeriesId(item)).filter((id): id is string => Boolean(id))
    );
    pruneHiddenSeriesIds(availableIds);
  }, [series]);

  const toggleSeriesVisibility = useCallback((id: string) => {
    setHiddenSeriesIds(prev => {
      if (prev.includes(id)) return prev.filter(item => item !== id);
      return [...prev, id];
    });
  }, []);

  const applyVisibility = useCallback(
    (items: ExtendedSeriesOptionsType[] | undefined) => {
      return applySeriesVisibility(items, hiddenSeriesIds);
    },
    [hiddenSeriesIds]
  );

  const visibleSeriesIds = useMemo(() => {
    if (!series?.length) return [];
    return series
      .map(item => getSeriesId(item))
      .filter((id): id is string => Boolean(id))
      .filter(id => !hiddenSeriesIds.includes(id));
  }, [hiddenSeriesIds, series]);

  return {
    hiddenSeriesIds,
    setHiddenSeriesIds,
    toggleSeriesVisibility,
    applyVisibility,
    visibleSeriesIds
  };
};

export default useSeriesVisibility;
